import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { FloatingFocusManager, FloatingOverlay, FloatingPortal, useClick, useDismiss, useFloating, useId, useInteractions, useRole, useTransitionStyles, } from "@floating-ui/react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Box } from "./Box";
import { mergeSx } from "../system/styles/sx";
import { useDefaultProps } from "..";
import { useTheme } from "./ThemeProvider";
export const dialogClasses = {
    root: "MinervaDialog-root",
    backdrop: "MinervaDialog-backdrop",
    anchor: "MinervaDialog-anchor",
};
/**
 * The dialog component is a floating overlay that is used to display content on top of the current page.
 * This component manages its own open state and dismisses when the user clicks outside of the dialog.
 * This dialog is meant to be built upon and extended to create more complex dialogs. Its default
 * implementation is a simple box with no width or height.
 */
export const Dialog = (props) => {
    const { open: _open, onOpenChange, disableCloseOnClickAway, disablePortal, className, sx, ...rest } = useDefaultProps({ component: "Dialog", props });
    const theme = useTheme();
    const [open, setOpen] = useState(_open);
    const { refs, context } = useFloating({
        open,
        onOpenChange: (open) => {
            setOpen(open);
            onOpenChange?.(open);
        },
    });
    const click = useClick(context);
    const dismiss = useDismiss(context, {
        outsidePressEvent: "mousedown",
        escapeKey: true,
        enabled: !disableCloseOnClickAway,
    });
    const role = useRole(context);
    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 100,
    });
    const { getFloatingProps } = useInteractions([click, dismiss, role]);
    const labelId = useId();
    const descriptionId = useId();
    useEffect(() => {
        setOpen(_open);
    }, [_open]);
    const dialog = (_jsx(FloatingOverlay, { lockScroll: true, className: dialogClasses.backdrop, style: {
            background: "rgba(0, 0, 0, 0.25)",
            zIndex: theme.zIndex.modal,
            display: "flex",
            ...transitionStyles,
        }, children: _jsx(FloatingFocusManager, { context: context, initialFocus: -1, children: _jsx(Box, { ref: refs.setFloating, "aria-labelledby": labelId, "aria-describedby": descriptionId, ...getFloatingProps(), ...rest, className: clsx([dialogClasses.root, className]), sx: mergeSx({
                    background: theme.palette.background.main,
                    color: theme.palette.background.onMain,
                    margin: "auto",
                }, sx) }) }) }));
    return (_jsxs(_Fragment, { children: [_jsx(Box, { ref: refs.setReference, sx: { display: "none" }, className: dialogClasses.anchor }), isMounted && (disablePortal ? dialog : _jsx(FloatingPortal, { children: dialog }))] }));
};
//# sourceMappingURL=Dialog.js.map